import React from 'react';

import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';

import prompt from 'antd-prompt';

import AddButton from 'components/action/AddButton';
import IndicatorEditionModal from 'components/Modal/IndicatorEditionModal';

import {
	useCreateIndicatorMutation,
	useDeleteIndicatorMutation,
	useUpdateIndicatorMutation,
} from '../../../graphql/generated';

import NavigationButton from 'components/action/NavigationButton';
import { Button, Flex, Tooltip, Typography } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Weight from 'components/controls/Weight';
import Table from 'components/Table';
import { faEdit } from '@fortawesome/pro-light-svg-icons';

import './Indicators.sass';

export const Indicators = (props: any) => {
	const [editedItem, setEditedItem] = React.useState<any | null>(null);

	const params: any = useParams();
	const [createIndicator] = useCreateIndicatorMutation();
	const [deleteData] = useDeleteIndicatorMutation();
	const [updateData] = useUpdateIndicatorMutation();
	const { t } = useTranslation();

	const { indicators = [] } = props;

	// Create
	const handleCreateIndicator = async () => {
		const name: any = await prompt({
			title: t("models.indicator.fields.name_explanation"),
			rules: [
				// check this link for more help: https://ant.design/components/form/#Validation-Rules
				{
					required: true,
					message: 'You must enter a name',
				},
			],
		});

		await createIndicator({
			variables: {
				projectId: params.id,
				name: name,
				weight: 1,
				description: '',
			},
		});

		await props.refetch();
	};

	// Update
	const handleUpdateIndicator = async (id: string, values: Object) => {
		await updateData({
			variables: {
				id: id,
				...values,
			},
		});

		await props.refetch();
	};

	// Delete
	const handleDelete = async (id: string | undefined) => {
		console.log('deleteData', id);

		if (id == null) {
			return;
		}

		await deleteData({
			variables: { id: id },
		});

		await props.refetch();
	};

	const columns = [
		{
			title: '',
			dataIndex: 'edit',
			key: 'edit',
			width: 40,
			render: (text, record) => (
				<Tooltip title={props.scenario ? null : 'Edit'}>
					<Button
						shape="circle"
						disabled={props.scenario ? true : false}
						style={{ opacity: props.scenario ? 0.2 : 1 }}
						onClick={
							props.disabled
								? () => { }
								: () => {
									setEditedItem(record);
								}
						}
					>
						<FontAwesomeIcon
							icon={faEdit}
							style={{
								cursor: props.disabled
									? 'not-allowed'
									: 'initial',
								opacity: props.disabled ? 0.5 : 1
							}}
						/>
					</Button>
				</Tooltip>
			)
		},
		{
			title: 'Name',
			dataIndex: 'name',
			key: 'name',
			className: 'w-100',
			sortable: true,
			defaultSortOrder: 'ascend',
			ellipsis: true,
			render: (text, record) => {
				return (
					<div className="name">
						{record.name}
						<div className="description">
							{record.description}
						</div>
					</div>
				);
			}
		},
		{
			title: 'Weight',
			dataIndex: 'weight',
			key: 'weight',
			sortable: true,
			width: 100,
			render: (text, record) => {
				return (
					<Weight
						style={{ marginRight: '0.5rem' }}
						onClick={(values: any) => {
							let newWeight = record.weight !== null && record.weight !== undefined ? record.weight + 1 : 1;
							if (newWeight > 3) newWeight = 0;
							handleUpdateIndicator(record.id, { weight: newWeight });
						}}
						disabled={!!props.scenario}
					>
						{record.weight}
					</Weight>
				);
			}
		}
	];

	return (
		<div className="Indicators pb-3">
			<Typography.Title level={2}>{t('pages.indicators.title')}</Typography.Title>
			{false && <Typography.Title level={3}>{t('pages.indicators.subtitle')}</Typography.Title>}

			<div className="mb-3 mt-4 w-100" style={{ maxWidth: '1200px' }}>
				<Table
					pagination={false}
					dataSource={indicators}
					columns={columns}
					size="small"
				/>
			</div>

			{editedItem && (
				<IndicatorEditionModal
					onHide={() => {
						setEditedItem(null);
					}}
					onDelete={() => {
						handleDelete(editedItem.id);
						setEditedItem(null);
					}}
					onSave={(values: Object) => {
						handleUpdateIndicator(editedItem.id, values);
						setEditedItem(null);
					}}
					id={editedItem?.id}
					values={{
						name: editedItem?.name,
						description: editedItem?.description,
						weight: editedItem?.weight !== null && editedItem?.weight !== undefined ? editedItem?.weight : 1,
					}}
				></IndicatorEditionModal>
			)}

			<Flex justify="center" align="center" className="w-100">
				<AddButton onClick={handleCreateIndicator}></AddButton>
				<NavigationButton className="ml-4" onClick={() => props.onGoNext('/projects/' + props?.id + '/' + t("models.constraint.slug"))}>
					Next step
				</NavigationButton>
			</Flex>

		</div>
	);
};
